/**
 * Content Engine seam — REAL AQI scoring + correction via the deployed
 * argoiq-engine, same pattern as `enrich.ts` / `argoWorker.ts`.
 *
 * When `VITE_CONTENT_ENGINE_URL` is set (it points at our own Netlify proxy,
 * e.g. `/api/content-engine` — see netlify/functions/content-engine.mts) we
 * POST `{ op: 'score'|'correct', content, channel, customer? }` with the
 * signed-in user's Firebase ID token and return the engine's AQI result.
 * The engine passcode lives ONLY in the Netlify function env — it never
 * reaches this client.
 *
 * When it is NOT set — local dev, demos, preview — `isConfigured()` is false
 * and callers fall back to a *deterministic local simulation* (seeded from
 * content length + keyword heuristics, stable for the same input) flagged
 * `{ simulated: true }` so the UI can show the honest 'Simulated' pill.
 * A 503 from the proxy ("engine not configured") or any network failure
 * downgrades to the same simulation — the buttons always produce a result.
 *
 * PUBLISHING IS DIFFERENT: op 'publish' (the engine's Zernio adapter for
 * LinkedIn/X) is NEVER simulated — a fake 'published' is a lie. Unconfigured
 * or failed publishes return honest 'not_configured' / 'error' results, and
 * the engine's server-side dry-run safety default is preserved: `dryRun` is
 * forwarded only when explicitly set, never defaulted to false here.
 */

import { auth } from '../firebase';

// ---------------------------------------------------------------------------
// Types + the AQI dimension contract
// ---------------------------------------------------------------------------

export type ContentChannel =
  | 'linkedin'
  | 'x_twitter'
  | 'reddit'
  | 'aeo_blog'
  | 'email_newsletter';

export interface AqiDimension {
  key: string;
  label: string;
  score: number;
  max: number;
}

/** A parsed AQI read: total 0–100 + per-dimension bars + the raw payload. */
export interface AqiScore {
  total: number;
  dimensions: AqiDimension[];
  raw: unknown;
}

/** An AQI read that self-reports whether it came from the real engine. */
export interface AqiScoreResult extends AqiScore {
  simulated: boolean;
}

/** A fix ("correct") result: the improved draft + its new score. */
export interface AqiFixResult {
  content: string;
  score: AqiScoreResult;
  simulated: boolean;
}

export interface ContentEngineInput {
  content: string;
  channel: ContentChannel;
  customer?: string;
}

/** A generated image (Imagen 3) or an honest reason it could not be made. */
export type ContentImageResult =
  | { imageBase64: string; mimeType: string }
  | { error: string };

/**
 * The engine's six AQI dimensions. Maxes sum to 100 and are used both for
 * the local simulation and as parse-time defaults when the engine omits a
 * dimension's max (the exact wire field names may drift — see parseAqiResult).
 */
export const AQI_DIMENSIONS = [
  { key: 'business_outcome', label: 'Business outcome', max: 20 },
  { key: 'brand_voice', label: 'Brand voice', max: 15 },
  { key: 'substantive_value', label: 'Substantive value', max: 20 },
  { key: 'audience_appropriateness', label: 'Audience appropriateness', max: 15 },
  { key: 'channel_native_execution', label: 'Channel-native execution', max: 15 },
  { key: 'compliance_safety', label: 'Compliance & safety', max: 15 },
] as const;

const TIMEOUT_MS = 30_000;

// Read at call time (not module load) so tests can stub the env and so the
// flag flips without a rebuild-order dependency.
function baseUrl(): string | undefined {
  const raw = import.meta.env.VITE_CONTENT_ENGINE_URL as string | undefined;
  const trimmed = raw?.trim().replace(/\/+$/, '');
  return trimmed ? trimmed : undefined;
}

/** True when the real Content Engine proxy is configured. */
export function isConfigured(): boolean {
  return Boolean(baseUrl());
}

/** Backend descriptor for status pills / debugging. */
export function contentEngineInfo(): { configured: boolean; baseUrl: string | null } {
  return { configured: isConfigured(), baseUrl: baseUrl() ?? null };
}

export class ContentEngineError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'ContentEngineError';
  }
}

// ---------------------------------------------------------------------------
// Public API — score + fix, falling back to simulation on any failure
// ---------------------------------------------------------------------------

/**
 * Score a draft against the AQI rubric. Never throws — an engine/network
 * failure downgrades to the deterministic local simulation.
 */
export async function scoreContent(input: ContentEngineInput): Promise<AqiScoreResult> {
  if (isConfigured()) {
    try {
      const raw = await postEngine('score', input);
      return { ...parseAqiResult(raw), simulated: false };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Argo content-engine] score failed, using local simulation:', err);
    }
  }
  return simulateScore(input.content, input.channel);
}

/**
 * Ask the engine to fix a draft and re-score it. Never throws — falls back
 * to the deterministic local simulation on any failure.
 */
export async function fixContent(input: ContentEngineInput): Promise<AqiFixResult> {
  if (isConfigured()) {
    try {
      const raw = await postEngine('correct', input);
      const fixed = extractCorrectedContent(raw) ?? input.content;
      return {
        content: fixed,
        score: { ...parseAqiResult(scorePayload(raw)), simulated: false },
        simulated: false,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[Argo content-engine] correct failed, using local simulation:', err);
    }
  }
  return simulateFix(input.content, input.channel);
}

/**
 * Generate a companion image for a visual asset (IG caption etc.) via the
 * engine's Imagen 3 endpoint. NO local simulation — a fabricated image is a
 * lie. When the proxy is unconfigured, or the engine refuses (safety block,
 * not-on-GCP, network), the result carries an honest `{ error }` and NEVER
 * an `imageBase64`. Never throws.
 */
export async function generateContentImage(
  prompt: string,
  aspectRatio?: string,
): Promise<ContentImageResult> {
  const trimmed = prompt.trim();
  if (!trimmed) return { error: 'Enter a prompt to generate an image.' };
  if (!isConfigured()) {
    return {
      error: 'Image generation requires the engine connection — see Admin → Integrations.',
    };
  }

  const url = baseUrl();
  if (!url) {
    return {
      error: 'Image generation requires the engine connection — see Admin → Integrations.',
    };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const bearer = await authBearer();
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
      body: JSON.stringify({
        op: 'image',
        prompt: trimmed,
        ...(aspectRatio ? { aspectRatio } : {}),
      }),
      signal: controller.signal,
    });
    const raw = (await res.json().catch(() => null)) as unknown;
    if (!res.ok) {
      return { error: engineImageError(raw, res.status) };
    }
    if (isRecord(raw)) {
      // The engine may itself return a structured refusal with a 200.
      if (typeof raw.error === 'string' && raw.error.trim()) {
        return { error: raw.error.trim() };
      }
      const imageBase64 =
        typeof raw.imageBase64 === 'string' && raw.imageBase64.trim()
          ? raw.imageBase64
          : undefined;
      if (imageBase64) {
        const mimeType =
          typeof raw.mimeType === 'string' && raw.mimeType.trim()
            ? raw.mimeType
            : 'image/png';
        return { imageBase64, mimeType };
      }
    }
    return { error: 'The engine returned no image.' };
  } catch (err) {
    if (controller.signal.aborted) {
      return { error: 'Image generation timed out — try again.' };
    }
    return { error: `Failed to reach the image engine: ${(err as Error).message}` };
  } finally {
    clearTimeout(timer);
  }
}

/** Pull an honest reason out of a non-OK image reply (structured or not). */
function engineImageError(raw: unknown, status: number): string {
  if (isRecord(raw) && typeof raw.error === 'string' && raw.error.trim()) {
    return raw.error.trim();
  }
  return `Image engine responded ${status}.`;
}

/** Before/after receipt for the Fix & re-score UI. Lift = after − before. */
export function fixReceipt(
  before: Pick<AqiScoreResult, 'total'>,
  after: Pick<AqiScoreResult, 'total'>,
): { before: number; after: number; lift: number } {
  return { before: before.total, after: after.total, lift: after.total - before.total };
}

// ---------------------------------------------------------------------------
// Wire plumbing — Firebase ID token bearer, same pattern as argoWorker.ts
// ---------------------------------------------------------------------------

async function authBearer(): Promise<string | undefined> {
  try {
    return await auth?.currentUser?.getIdToken();
  } catch {
    return undefined;
  }
}

async function postEngine(
  op: 'score' | 'correct' | 'publish',
  input: ContentEngineInput & { dryRun?: boolean },
): Promise<unknown> {
  const url = baseUrl();
  if (!url) throw new ContentEngineError('Content Engine is not configured.');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const bearer = await authBearer();
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
      body: JSON.stringify({
        op,
        content: input.content,
        channel: input.channel,
        ...(input.customer ? { customer: input.customer } : {}),
        // Forwarded ONLY when explicitly boolean. Omitted → the engine's
        // server-side dry-run safety default rules; we NEVER default to false.
        ...(typeof input.dryRun === 'boolean' ? { dryRun: input.dryRun } : {}),
      }),
      signal: controller.signal,
    });
    if (!res.ok) {
      // 503 = proxy says "engine not configured" — treated as unconfigured.
      throw new ContentEngineError(`Content Engine responded ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as unknown;
  } catch (err) {
    if (err instanceof ContentEngineError) throw err;
    throw new ContentEngineError('Failed to reach the Content Engine.', err);
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Tolerant parsing — the engine's exact field names may drift
// ---------------------------------------------------------------------------

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Normalize a wire dimension name to our snake_case keys. */
function normalizeKey(name: string): string {
  return name.trim().toLowerCase().replace(/[\s&/-]+/g, '_').replace(/_+/g, '_');
}

function dimensionScore(entry: unknown, defaultMax: number): { score: number; max: number } | null {
  if (isRecord(entry)) {
    const score =
      asNumber(entry.score) ?? asNumber(entry.value) ?? asNumber(entry.points);
    if (score === undefined) return null;
    const max =
      asNumber(entry.max) ?? asNumber(entry.max_score) ?? asNumber(entry.out_of) ?? defaultMax;
    return { score: clamp(score, 0, max), max };
  }
  const score = asNumber(entry);
  if (score === undefined) return null;
  return { score: clamp(score, 0, defaultMax), max: defaultMax };
}

/**
 * Extract `{ total, dimensions[], raw }` from whatever shape the engine
 * returns. Understands array-of-dimension objects, keyed dimension maps
 * (under `dimensions`/`scores`/`breakdown` or at the top level), numeric or
 * `{ score, max }` entries, and several total aliases. Never throws; unknown
 * shapes degrade to `{ total: 0, dimensions: [], raw }`.
 */
export function parseAqiResult(raw: unknown): AqiScore {
  const obj = isRecord(raw) ? raw : {};
  const dims: AqiDimension[] = [];

  // 1) Array form: dimensions: [{ key|name|dimension, score, max, label? }]
  const arrayish = [obj.dimensions, obj.scores, obj.breakdown].find(Array.isArray) as
    | unknown[]
    | undefined;
  if (arrayish) {
    for (const item of arrayish) {
      if (!isRecord(item)) continue;
      const rawKey =
        (typeof item.key === 'string' && item.key) ||
        (typeof item.name === 'string' && item.name) ||
        (typeof item.dimension === 'string' && item.dimension) ||
        '';
      if (!rawKey) continue;
      const key = normalizeKey(rawKey);
      const spec = AQI_DIMENSIONS.find((d) => d.key === key);
      const parsed = dimensionScore(item, spec?.max ?? 20);
      if (!parsed) continue;
      dims.push({
        key,
        label:
          spec?.label ?? (typeof item.label === 'string' ? item.label : rawKey),
        score: parsed.score,
        max: parsed.max,
      });
    }
  }

  // 2) Map form: known keys inside dimensions/scores/breakdown or top-level.
  if (dims.length === 0) {
    const containers = [obj.dimensions, obj.scores, obj.breakdown, obj].filter(isRecord);
    for (const spec of AQI_DIMENSIONS) {
      for (const container of containers) {
        if (!(spec.key in container)) continue;
        const parsed = dimensionScore(container[spec.key], spec.max);
        if (parsed) {
          dims.push({ key: spec.key, label: spec.label, ...parsed });
          break;
        }
      }
    }
  }

  const explicitTotal =
    asNumber(obj.total) ??
    asNumber(obj.aqi) ??
    asNumber(obj.aqi_total) ??
    asNumber(obj.total_score) ??
    asNumber(obj.overall) ??
    asNumber(obj.score);
  const maxSum = dims.reduce((s, d) => s + d.max, 0);
  const derivedTotal =
    maxSum > 0 ? (dims.reduce((s, d) => s + d.score, 0) / maxSum) * 100 : 0;
  const total = clamp(Math.round(explicitTotal ?? derivedTotal), 0, 100);

  return { total, dimensions: dims, raw };
}

/** The `/correct` reply may nest its new score — pick the likeliest container. */
function scorePayload(raw: unknown): unknown {
  if (!isRecord(raw)) return raw;
  for (const key of ['new_score', 'score', 'aqi', 'result']) {
    if (isRecord(raw[key])) return raw[key];
  }
  return raw;
}

/** Pull the improved draft out of a `/correct` reply, whatever it's called. */
function extractCorrectedContent(raw: unknown): string | undefined {
  if (!isRecord(raw)) return undefined;
  for (const key of ['corrected_content', 'content', 'corrected', 'improved', 'draft', 'text']) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Publish — the engine's POST /publish (Zernio adapter, LinkedIn/X).
//
// UNLIKE score/correct there is NO local simulation here: a fake 'published'
// is a lie. Unconfigured → an honest 'not_configured' result; any failure →
// an honest 'error' result. The engine defaults to dryRun server-side for
// safety; we forward `dryRun` only when the caller set it explicitly and
// NEVER default it to false.
// ---------------------------------------------------------------------------

/** Channels the engine's Zernio adapter can post to. */
export type PublishChannel = Extract<ContentChannel, 'linkedin' | 'x_twitter'>;

export const PUBLISH_CHANNELS: readonly PublishChannel[] = ['linkedin', 'x_twitter'];

/** True when the channel is one the engine can actually publish to. */
export function isPublishChannel(channel: string): channel is PublishChannel {
  return (PUBLISH_CHANNELS as readonly string[]).includes(channel);
}

/** Human label for a publish channel ('linkedin' → 'LinkedIn'). */
export function publishChannelLabel(channel: PublishChannel): string {
  return channel === 'linkedin' ? 'LinkedIn' : 'X';
}

export interface PublishInput {
  content: string;
  channel: PublishChannel;
  customer?: string;
  /**
   * Explicit dry-run override. OMITTED by default so the engine's
   * server-side dry-run safety default rules — never pass `false` unless a
   * human explicitly asked for a live post.
   */
  dryRun?: boolean;
}

/** The four honest outcomes of a publish attempt. */
export type PublishStatus = 'published' | 'dry_run' | 'error' | 'not_configured';

/** Parsed publish outcome: status + url/message extracted, raw kept. */
export interface PublishResult {
  status: PublishStatus;
  /** Live post URL when the engine returned one. */
  url: string | null;
  /** Engine message / error detail when present. */
  message: string | null;
  raw: unknown;
}

export const PUBLISH_NOT_CONFIGURED_MESSAGE =
  'Publishing requires the engine connection (VITE_CONTENT_ENGINE_URL) — nothing was posted.';

/**
 * Publish a draft through the engine's /publish endpoint. Never throws and
 * never simulates: unconfigured → 'not_configured', failure → 'error',
 * otherwise the tolerant-parsed engine outcome ('published' | 'dry_run').
 */
export async function publishContent(input: PublishInput): Promise<PublishResult> {
  if (!isConfigured()) {
    return {
      status: 'not_configured',
      url: null,
      message: PUBLISH_NOT_CONFIGURED_MESSAGE,
      raw: null,
    };
  }
  try {
    const raw = await postEngine('publish', input);
    return parsePublishResult(raw);
  } catch (err) {
    return {
      status: 'error',
      url: null,
      message: err instanceof Error ? err.message : 'Failed to reach the Content Engine.',
      raw: null,
    };
  }
}

/** First non-empty string among `keys` across the given containers. */
function firstString(
  containers: Record<string, unknown>[],
  keys: string[],
): string | null {
  for (const c of containers) {
    for (const k of keys) {
      const v = c[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return null;
}

/** First explicit boolean among `keys` across the given containers. */
function firstBoolean(
  containers: Record<string, unknown>[],
  keys: string[],
): boolean | undefined {
  for (const c of containers) {
    for (const k of keys) {
      const v = c[k];
      if (typeof v === 'boolean') return v;
    }
  }
  return undefined;
}

/**
 * Extract `{ status, url, message, raw }` from whatever shape the engine's
 * /publish returns. Honesty-ordered:
 *   1. an explicit dry-run flag wins — a dry run is never rendered as live;
 *   2. explicit error signals → 'error';
 *   3. explicit published/success signals (or a post URL with dryRun:false)
 *      → 'published';
 *   4. an ambiguous 200 reads as 'dry_run' — the engine's server-side safety
 *      default — because claiming a live post we cannot verify is a lie.
 * Never throws; raw is always preserved.
 */
export function parsePublishResult(raw: unknown): PublishResult {
  const obj = isRecord(raw) ? raw : {};
  const containers = [obj, ...(isRecord(obj.result) ? [obj.result] : [])];

  const url = firstString(containers, ['url', 'post_url', 'postUrl', 'permalink', 'link']);
  const message = firstString(containers, ['message', 'detail', 'error', 'reason']);
  const statusStr = (firstString(containers, ['status', 'state']) ?? '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const dryFlag = firstBoolean(containers, ['dryRun', 'dry_run', 'dryrun']);

  if (dryFlag === true || statusStr === 'dry_run' || statusStr === 'dryrun') {
    return { status: 'dry_run', url, message, raw };
  }

  const errored =
    firstBoolean(containers, ['ok', 'success']) === false ||
    typeof obj.error === 'string' ||
    statusStr === 'error' ||
    statusStr === 'failed' ||
    statusStr === 'failure';
  if (errored) {
    return {
      status: 'error',
      url,
      message: message ?? 'Engine reported a publish error.',
      raw,
    };
  }

  const published =
    firstBoolean(containers, ['published', 'posted']) === true ||
    firstBoolean(containers, ['ok', 'success']) === true ||
    statusStr === 'published' ||
    statusStr === 'posted' ||
    statusStr === 'live' ||
    statusStr === 'success' ||
    (dryFlag === false && Boolean(url));
  if (published) {
    return { status: 'published', url, message, raw };
  }

  return {
    status: 'dry_run',
    url,
    message:
      message ??
      'Engine reply did not confirm a live post — treated as a dry run (engine safety default).',
    raw,
  };
}

/**
 * One honest human-readable line per publish outcome — shared by the
 * executor's action result and the draft-record rendering so the two can
 * never drift apart (and a dry run is never phrased as a live post).
 */
export function describePublishOutcome(
  outcome: Pick<PublishResult, 'status' | 'url' | 'message'>,
  channel: PublishChannel,
): string {
  switch (outcome.status) {
    case 'published':
      return `Published to ${publishChannelLabel(channel)}${outcome.url ? ` — ${outcome.url}` : ''}`;
    case 'dry_run':
      return 'Queued to engine — DRY RUN (no live post)';
    case 'not_configured':
      return PUBLISH_NOT_CONFIGURED_MESSAGE;
    case 'error':
      return `Publish failed: ${outcome.message ?? 'engine error'} — nothing was posted.`;
  }
}

// ---------------------------------------------------------------------------
// Deterministic local simulation — honest, stable, offline
// ---------------------------------------------------------------------------

/** Stable 0–1 hash so the same draft always reads the same way. */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 1000) / 1000;
}

const CTA_RE =
  /\b(book|schedule|reply|sign\s?up|subscribe|learn more|get started|demo|register|join|download|reach out)\b/i;
const RISKY_RE = /\b(guaranteed?|risk[-\s]?free|100%|no obligation|can't lose)\b/i;
const AUDIENCE_RE = /\byou(r|'re)?\b/i;

/** Channel-native length bands for the simulation's execution heuristic. */
const CHANNEL_BAND: Record<ContentChannel, [number, number]> = {
  linkedin: [400, 1800],
  x_twitter: [40, 280],
  reddit: [300, 4000],
  aeo_blog: [800, 12000],
  email_newsletter: [300, 1500],
};

/** 0–1 heuristic per dimension, from content length + keyword signals. */
function heuristicFor(key: string, content: string, channel: ContentChannel): number {
  const len = content.trim().length;
  switch (key) {
    case 'business_outcome':
      return CTA_RE.test(content) ? 0.9 : 0.35;
    case 'brand_voice': {
      const shouting = (content.match(/!/g) ?? []).length > 3 || /\b[A-Z]{5,}\b/.test(content);
      return shouting ? 0.4 : 0.8;
    }
    case 'substantive_value': {
      let h = 0.3;
      if (/\d/.test(content)) h += 0.3;
      if (len >= 280) h += 0.3;
      if (/[%$]/.test(content)) h += 0.1;
      return Math.min(1, h);
    }
    case 'audience_appropriateness':
      return AUDIENCE_RE.test(content) ? 0.85 : 0.4;
    case 'channel_native_execution': {
      const [lo, hi] = CHANNEL_BAND[channel];
      return len >= lo && len <= hi ? 0.85 : 0.4;
    }
    case 'compliance_safety':
      return RISKY_RE.test(content) ? 0.3 : 0.9;
    default:
      return 0.5;
  }
}

/**
 * Deterministic simulated score. `seedText` defaults to the content itself;
 * simulateFix passes the ORIGINAL draft so before/after receipts move only
 * on real heuristic deltas, not hash noise.
 */
function simulateScore(
  content: string,
  channel: ContentChannel,
  seedText: string = content,
): AqiScoreResult {
  const dims: AqiDimension[] = AQI_DIMENSIONS.map((spec) => {
    const h = heuristicFor(spec.key, content, channel);
    const r = hash01(`${seedText}|${channel}|${spec.key}`);
    const score = clamp(Math.round(spec.max * (0.7 * h + 0.3 * r)), 0, spec.max);
    return { key: spec.key, label: spec.label, score, max: spec.max };
  });
  const total = clamp(
    Math.round(dims.reduce((s, d) => s + d.score, 0)),
    0,
    100,
  );
  return {
    total,
    dimensions: dims,
    raw: { simulated: true, note: 'Deterministic local heuristic — no engine call.' },
    simulated: true,
  };
}

/** Channel-appropriate CTA line the simulated fix appends when one is missing. */
const CHANNEL_CTA: Record<ContentChannel, string> = {
  linkedin: 'If this resonates, reply below or book 15 minutes — happy to compare notes.',
  x_twitter: 'Reply if you want the playbook.',
  reddit: 'Happy to share more detail in the comments — reply with your setup.',
  aeo_blog: 'Ready to see it on your data? Book a demo to get started.',
  email_newsletter: 'Hit reply to book a quick walkthrough — no deck, just your questions.',
};

/**
 * Deterministic simulated fix: tidies whitespace and adds a channel-native
 * CTA when missing, then re-scores with the ORIGINAL draft as the hash seed
 * so the receipt's lift reflects the heuristic improvements only.
 */
function simulateFix(content: string, channel: ContentChannel): AqiFixResult {
  let fixed = content.replace(/\n{3,}/g, '\n\n').trim();
  if (!CTA_RE.test(fixed)) {
    fixed = fixed ? `${fixed}\n\n${CHANNEL_CTA[channel]}` : CHANNEL_CTA[channel];
  }
  return {
    content: fixed,
    score: simulateScore(fixed, channel, content),
    simulated: true,
  };
}
